/**
 * 65.49's lock tone (the coordinator's brief: "a seeker tone that tells the holder its state … distinct from the music's
 * stingers and readable over gunfire"): what the anti-dropship missile's seeker sounds like to the one holding it, from its
 * lock (PlayerState.missileLock: searching, tracking — the hold's share rising — locked), and the warning a person aboard a
 * ship hears when an enemy seeker tracks or holds it. Pure: each frame's tone as numbers (lockTone.ts plays them).
 *
 * Readable over gunfire: a narrow tone in 0.7–2 kHz, where the shots' broadband energy is thinner, on the effects bus (it
 * does not duck under the guns as the music does). Distinct from the stingers: no musical interval — a growl rising, a
 * beep rate climbing, a warble between two tones a non-musical ratio apart — and it stops when the missile goes down.
 *
 * Three voices for the owner to pick (sound_sheets/lock_tone.html):
 *   - `growl`: the classic air-to-air seeker — a low rough growl searching, rising in pitch and brightness as the hold
 *     builds, a clean high tone locked;
 *   - `beeps`: a slow tick searching, beeps speeding up as the hold builds, a solid tone locked;
 *   - `warble`: a faint double pip searching, a warble between two tones speeding up, a fast bright warble locked.
 */

export type LockToneVoice = 'growl' | 'beeps' | 'warble';
export const LOCK_TONE_VOICES: readonly LockToneVoice[] = ['growl', 'beeps', 'warble'];
/** The owner's pick (Oct 21: "Growl"); in play `?locktone=beeps` or `warble` still tries another (sound_sheets/lock_tone.html). */
export const LOCK_TONE_DEFAULT: LockToneVoice = 'growl';
/**
 * The holder's tone, all three voices, over the levels each is written at below: −9 dB (the owner, Oct 21: "We can make the
 * lock tone a little quieter though super loud right now"). The ship's warning is not scaled (he: it sounds good).
 */
export const LOCK_TONE_LEVEL = 0.355;
export type LockToneState = 'search' | 'track' | 'lock';

/**
 * One frame of the tone: its oscillator's wave and pitch (Hz), its level (the effects bus's 0–1), the low-pass over it (Hz),
 * a modulation at audio rate (`am` a rough or beeping level — `depth` 0–1, a square gate at 1; `fm` a warble — `depth` the
 * second tone's ratio to `hz`) and a slow pulse gated per frame (a beep every `period` s lasting `width` s: the searching
 * ticks). `gain` 0: silent.
 */
export interface LockToneFrame {
  wave: OscillatorType;
  hz: number;
  gain: number;
  cutoff: number;
  mod: { kind: 'none' | 'am' | 'fm'; rate: number; depth: number; square?: boolean };
  pulse?: { period: number; width: number };
}

const lerp = (a: number, b: number, k: number) => a + (b - a) * Math.max(0, Math.min(1, k));

/** The holder's tone for `voice` in `state`, `progress` the hold's share (0 … 1) while tracking, at LOCK_TONE_LEVEL. */
export function lockToneFrame(voice: LockToneVoice, state: LockToneState, progress = 0): LockToneFrame {
  const f = voiceFrame(voice, state, progress);
  return { ...f, gain: f.gain * LOCK_TONE_LEVEL };
}

function voiceFrame(voice: LockToneVoice, state: LockToneState, progress: number): LockToneFrame {
  const k = state === 'track' ? progress : state === 'lock' ? 1 : 0;
  if (voice === 'growl') {
    if (state === 'lock') return { wave: 'sawtooth', hz: 1250, gain: 0.16, cutoff: 4200, mod: { kind: 'am', rate: 7, depth: 0.08 } };   // (clean: a breath of vibrato)
    // searching a low rough growl; tracking it climbs in pitch and opens up as the hold builds
    return { wave: 'sawtooth', hz: lerp(330, 880, k), gain: lerp(0.06, 0.13, k), cutoff: lerp(900, 3200, k), mod: { kind: 'am', rate: lerp(26, 14, k), depth: lerp(0.65, 0.45, k) } };
  }
  if (voice === 'beeps') {
    if (state === 'lock') return { wave: 'square', hz: 1560, gain: 0.12, cutoff: 5000, mod: { kind: 'none', rate: 0, depth: 0 } };
    if (state === 'search') return { wave: 'square', hz: 880, gain: 0.07, cutoff: 3500, mod: { kind: 'none', rate: 0, depth: 0 }, pulse: { period: 1.2, width: 0.06 } };
    return { wave: 'square', hz: lerp(1040, 1240, k), gain: lerp(0.09, 0.12, k), cutoff: 4500, mod: { kind: 'am', rate: lerp(2.5, 10, k), depth: 1, square: true } };
  }
  // warble: two tones 1.41 apart (between a fourth and a fifth: no chord the music plays)
  if (state === 'lock') return { wave: 'triangle', hz: 1100, gain: 0.16, cutoff: 6000, mod: { kind: 'fm', rate: 14, depth: 1.41, square: true } };
  if (state === 'search') return { wave: 'triangle', hz: 690, gain: 0.08, cutoff: 4000, mod: { kind: 'fm', rate: 12, depth: 1.41, square: true }, pulse: { period: 1.5, width: 0.17 } };
  return { wave: 'triangle', hz: lerp(690, 1000, k), gain: lerp(0.1, 0.14, k), cutoff: 5000, mod: { kind: 'fm', rate: lerp(3, 11, k), depth: 1.41, square: true } };
}

/**
 * The warning a person aboard a ship hears (the one voice; not the holder's): an enemy seeker tracking it, a double beep
 * twice a second; holding it, a rapid high beep. High and short — above the holder's tones and the ship's own noise.
 */
export function shipWarningFrame(state: 'track' | 'lock'): LockToneFrame {
  return state === 'lock'
    ? { wave: 'square', hz: 2000, gain: 0.11, cutoff: 6000, mod: { kind: 'am', rate: 8, depth: 1, square: true } }
    : { wave: 'square', hz: 1800, gain: 0.09, cutoff: 6000, mod: { kind: 'am', rate: 8, depth: 1, square: true }, pulse: { period: 0.5, width: 0.22 } };
}

/** Whether a slow pulse is sounding at time `t` (s). */
export function pulseOn(pulse: LockToneFrame['pulse'], t: number): boolean {
  return !pulse || ((t % pulse.period) + pulse.period) % pulse.period < pulse.width;
}

/** The hold's share from a lock (MissileLock.held over the hold it needs). */
export function lockProgress(held: number, hold: number): number {
  return hold > 0 ? Math.max(0, Math.min(1, held / hold)) : 0;
}
